import axios, { AxiosError, AxiosResponse, Canceler } from 'axios';
import { userNotLogged } from 'store/actions/user';
import store from 'store/store';

type IPostFileResult<T> = {
  data?: T;
  message?: string;
};

type IPostResult<T> = IPostFileResult<T> & {
  ok: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-var-requires
const qs = require('qs');

let notify: React.Dispatch<React.SetStateAction<string | null>>;

export const initRequestErrorNotification = (setNotification: React.Dispatch<React.SetStateAction<string | null>>) => {
  notify = setNotification;
};

export const notifyError = (message: string) => {
  notify?.(message);
};

const isResponseOK = (response: AxiosResponse) => response.data.result === 'OK';

// The 400 body of input-filter failures is { result, message: { field: { rule: text } } }:
// message is an OBJECT, and notify() feeds a React child — passing it through
// unmounted the whole app ("Objects are not valid as a React child").
const formatErrorMessage = (data: any): string => {
  if (typeof data === 'string') {
    return data;
  }
  if (typeof data?.error === 'string') {
    return data.error;
  }
  if (typeof data?.error?.message === 'string') {
    return data.error.message;
  }
  const message = data?.message;
  if (typeof message === 'string') {
    return message;
  }
  if (message && typeof message === 'object') {
    return Object.entries(message)
      .map(
        ([field, rules]) =>
          `${field}: ${typeof rules === 'string' ? rules : Object.values(rules as object).join(', ')}`,
      )
      .join(' · ');
  }
  return 'Bad request';
};

const handleError = (err: unknown, path: string) => {
  const error = err as AxiosError;
  console.log('error.response: ', error.response);

  if (error.response?.status === 500) {
    notify?.(path + ': ' + formatErrorMessage(error.response?.data));
  } else if (error.response?.status === 400) {
    notify?.(formatErrorMessage(error.response?.data));
  }
};

export async function get<T>(path: string, query: object = {}, cancelToken?: (c: Canceler) => void) {
  try {
    const response = await axios.get<T>(path, {
      params: query,
      timeout: 999999,
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
      cancelToken: new axios.CancelToken(function executor(c) {
        if (cancelToken) {
          cancelToken(c);
        }
      }),
    });
    const reposeDataLogged = response.data as { logged?: boolean };
    if (typeof reposeDataLogged.logged !== 'undefined') {
      // Session expired or user not logged. Trigger auth flow and signal callers via catch path.
      store.dispatch(userNotLogged());
      throw new Error('UNAUTHENTICATED');
    }
    return response.data;
  } catch (error) {
    handleError(error, path);
    return {} as T;
  }
}

export async function post<T extends {}>(path: string, payload: any): Promise<IPostResult<T>> {
  try {
    const response = await axios.post<T>(path, payload);

    return {
      ok: isResponseOK(response),
      data: response.data,
    };
  } catch (error) {
    handleError(error, path);
    return {
      ok: false,
    };
  }
}

export async function postFile<T extends {}>(path: string, payload: File): Promise<IPostFileResult<T>> {
  try {
    const formData = new FormData();
    formData.append('file', payload);
    const response = await axios.post<T>(path, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return {
      data: response.data,
    };
  } catch (error) {
    handleError(error, path);
    return {};
  }
}

export async function postMultiFile<T extends {}>(
  path: string,
  payload: { fileName: string; file: File }[],
): Promise<IPostFileResult<T>> {
  try {
    const formData = new FormData();
    payload.forEach((data) => {
      formData.append(data.fileName, data.file);
    });
    const response = await axios.post<T>(path, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return {
      data: response.data,
    };
  } catch (error) {
    handleError(error, path);
    // If the error response contains an error message, return it
    if ((error as any).response?.data?.error) {
      return { data: (error as any).response.data as T };
    }
    return {};
  }
}

export async function put<T extends {}>(path: string, payload: any): Promise<IPostResult<T>> {
  try {
    const response = await axios.put<T>(path, payload);

    return {
      data: response.data,
      ok: isResponseOK(response),
    };
  } catch (error) {
    handleError(error, path);
    return {
      ok: false,
    };
  }
}

export async function del<T extends {}>(path: string): Promise<IPostResult<T>> {
  try {
    const response = await axios.delete(path);

    return {
      data: response.data,
      ok: isResponseOK(response),
    };
  } catch (error) {
    handleError(error, path);
    return {
      ok: false,
    };
  }
}
